import * as React from 'react'
import clsx from 'clsx'
import { CursorCoordinates } from '../../../models/cursorCoordinates'
import { useGridApiEventHandler } from '../../utils/useGridApiEventHandler'
import { GridEventListener } from '../../../models/events'
import { GridPrivateApiCommunity } from '../../../models/api/gridApiCommunity'
import { gridColumnReorderDragColSelector } from './columnReorderSelector'
import style from '../../../style'
import { GridStateInitializer } from '../../utils/useGridInitializeState'
import { ColumnOrderState } from '../../../models/gridReactTable'
import { DataGridProcessedProps } from '../../../models/props/DataGridProps'
import { GridColumnOrderChangedParams } from '../../../models/params/gridColumnOrderChangedParams'

const CURSOR_MOVE_DIRECTION_LEFT = 'left'
const CURSOR_MOVE_DIRECTION_RIGHT = 'right'

const getCursorMoveDirectionX = (
  currentCoordinates: CursorCoordinates,
  nextCoordinates: CursorCoordinates
) => {
  return currentCoordinates.x <= nextCoordinates.x
    ? CURSOR_MOVE_DIRECTION_RIGHT
    : CURSOR_MOVE_DIRECTION_LEFT
}

const hasCursorPositionChanged = (
  currentCoordinates: CursorCoordinates,
  nextCoordinates: CursorCoordinates
): boolean =>
  currentCoordinates.x !== nextCoordinates.x ||
  currentCoordinates.y !== nextCoordinates.y

const reorderColumn = (
  draggedColumnId: string,
  targetColumnId: string,
  columnOrder: string[]
): ColumnOrderState => {
  columnOrder.splice(
    columnOrder.indexOf(targetColumnId),
    0,
    columnOrder.splice(columnOrder.indexOf(draggedColumnId), 1)[0] as string
  )
  return [...columnOrder]
}

const reorderColumnByTragetIndex = (
  draggedColumnId: string,
  targetIndex: number,
  columnOrder: string[]
): ColumnOrderState => {
  columnOrder.splice(
    targetIndex,
    0,
    columnOrder.splice(columnOrder.indexOf(draggedColumnId), 1)[0] as string
  )
  return [...columnOrder]
}

// @ts-ignore
export const columnReorderStateInitializer: GridStateInitializer = (
  state,
  _,
  apiRef
) => {
  const columns = apiRef.current.tableInstance
    .getAllLeafColumns()
    .map(one => one.id)
  apiRef.current.tableInstance.setColumnOrder(columns)
  return {
    ...state,
    columnReorder: { dragCol: '' },
  }
}

export const useGridColumnReorder = (
  apiRef: React.MutableRefObject<GridPrivateApiCommunity>,
  props: Pick<
    DataGridProcessedProps,
    | 'disableColumnReorder'
    | 'keepColumnPositionIfDraggedOutside'
    | 'classes'
    | 'columns'
    | 'onColumnOrderChanged'
  >
): void => {
  const dragColNode = React.useRef<HTMLElement | null>(null)
  const originColumnIndex = React.useRef<number | null>(null)
  const cursorPosition = React.useRef<CursorCoordinates>({
    x: 0,
    y: 0,
  })
  const removeDnDStylesTimeout = React.useRef<any>()

  const classes = React.useMemo(
    () => ({
      columnHeaderDragging: clsx(
        style['columnHeader--dragging'],
        props?.classes?.['columnHeader--dragging']
      ),
    }),
    [props.classes]
  )

  React.useEffect(() => {
    return () => {
      clearTimeout(removeDnDStylesTimeout.current)
    }
  }, [])

  const handleDragStart = React.useCallback<
    GridEventListener<'columnHeaderDragStart'>
  >(
    (params, event) => {
      if (props.disableColumnReorder || params.disableReorder) {
        return
      }
      // Prevent drag events propagation.
      // For more information check here https://github.com/mui/mui-x/issues/2680.
      event.stopPropagation()
      dragColNode.current = event.currentTarget
      dragColNode.current.classList.add(classes.columnHeaderDragging)
      apiRef.current.setState(state => ({
        ...state,
        columnReorder: { ...state.columnReorder, dragCol: params.field },
      }))
      apiRef.current.forceUpdate()
      removeDnDStylesTimeout.current = setTimeout(() => {
        dragColNode.current!.classList.remove(classes.columnHeaderDragging)
      })
      const allColumns = apiRef.current.tableInstance.getState().columnOrder
      originColumnIndex.current = allColumns.indexOf(params.field)
    },
    [props.disableColumnReorder, classes.columnHeaderDragging, apiRef]
  )

  const handleDragEnter = React.useCallback<
    GridEventListener<'cellDragEnter' | 'columnHeaderDragEnter'>
  >((params, event) => {
    event.preventDefault()
    // Prevent drag events propagation.
    // For more information check here https://github.com/mui/mui-x/issues/2680.
    event.stopPropagation()
  }, [])

  const handleDragOver = React.useCallback<
    GridEventListener<'cellDragOver' | 'columnHeaderDragOver'>
  >(
    (params, event) => {
      const dragColField = gridColumnReorderDragColSelector(apiRef)
      if (!dragColField) {
        return
      }

      event.preventDefault()
      // Prevent drag events propagation.
      // For more information check here https://github.com/mui/mui-x/issues/2680.
      event.stopPropagation()

      const coordinates = { x: event.clientX, y: event.clientY }

      if (
        params.field !== dragColField &&
        hasCursorPositionChanged(cursorPosition.current, coordinates)
      ) {
        const allColumns = apiRef.current.tableInstance.getState().columnOrder
        const targetColField = params.field
        const targetColIndex = allColumns.indexOf(targetColField)
        const dragColIndex = allColumns.indexOf(dragColField)
        let targetCol
        try {
          targetCol = apiRef.current.tableInstance.getColumn(targetColField)
        } catch (e) {
          targetCol = {}
        }

        const cursorMoveDirectionX = getCursorMoveDirectionX(
          cursorPosition.current,
          coordinates
        )
        const hasMovedLeft =
          cursorMoveDirectionX === CURSOR_MOVE_DIRECTION_LEFT &&
          targetColIndex < dragColIndex
        const hasMovedRight =
          cursorMoveDirectionX === CURSOR_MOVE_DIRECTION_RIGHT &&
          dragColIndex < targetColIndex

        if (hasMovedLeft || hasMovedRight) {
          let canBeReordered = false
          if (!targetCol?.columnDef?.meta?.disableReorder) {
            canBeReordered = true
          }

          if (canBeReordered) {
            const reordered = reorderColumn(
              dragColField,
              targetColField,
              allColumns
            )
            apiRef.current.tableInstance.setColumnOrder(reordered)
          }
        }
        cursorPosition.current = coordinates
      }
    },
    [apiRef]
  )

  const handleDragEnd = React.useCallback<
    GridEventListener<'columnHeaderDragEnd'>
  >(
    (params, event): void => {
      const dragColField = gridColumnReorderDragColSelector(apiRef)
      if (props.disableColumnReorder || !dragColField) {
        return
      }
      const allColumns = apiRef.current.tableInstance.getState().columnOrder

      event.preventDefault()
      // Prevent drag events propagation.
      // For more information check here https://github.com/mui/mui-x/issues/2680.
      event.stopPropagation()

      clearTimeout(removeDnDStylesTimeout.current)
      dragColNode.current = null

      // Check if the column was dropped outside the grid.
      if (
        event.dataTransfer.dropEffect === 'none' &&
        !props.keepColumnPositionIfDraggedOutside
      ) {
        // Accessing params.field may contain the wrong field as header elements are reused
        const reordered = reorderColumnByTragetIndex(
          dragColField,
          originColumnIndex.current!,
          allColumns
        )
        apiRef.current.tableInstance.setColumnOrder(reordered)
      } else {
        // Emit the columnOrderChanged event only once when the reordering stops
        const columnOrderChangedParams: GridColumnOrderChangedParams = {
          targetIndex: allColumns.indexOf(params.field),
          oldIndex: originColumnIndex.current!,
          column: params.field,
        }
        apiRef.current.publishEvent(
          'columnOrderChanged',
          columnOrderChangedParams
        )
      }
      originColumnIndex.current = null
      apiRef.current.setState(state => ({
        ...state,
        columnReorder: { ...state.columnReorder, dragCol: '' },
      }))
      apiRef.current.forceUpdate()
    },
    [
      props.disableColumnReorder,
      props.keepColumnPositionIfDraggedOutside,
      apiRef,
    ]
  )

  React.useEffect(() => {
    const columns = apiRef.current.tableInstance
      .getAllLeafColumns()
      .map(one => one.id)
    apiRef.current.tableInstance.setColumnOrder(columns)
    // we want to recompute columns order when columns changes
  }, [apiRef, props.columns])

  useGridApiEventHandler(apiRef, 'columnHeaderDragStart', handleDragStart)
  useGridApiEventHandler(apiRef, 'columnHeaderDragEnter', handleDragEnter)
  useGridApiEventHandler(apiRef, 'columnHeaderDragOver', handleDragOver)
  useGridApiEventHandler(apiRef, 'columnHeaderDragEnd', handleDragEnd)
  useGridApiEventHandler(apiRef, 'cellDragEnter', handleDragEnter)
  useGridApiEventHandler(apiRef, 'cellDragOver', handleDragOver)
  useGridApiEventHandler(
    apiRef,
    'columnOrderChanged',
    props.onColumnOrderChanged
  )
}
